import React from "react";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";

import Layout from "../components/Layout";
import SearchEngineOptimization from "../components/SEO";
import Background from "../components/Background/Background";
import Testimonials from "../components/Repeating/Testimonials";
import ValueProps from "../components/Repeating/ValueProps";
import About from "../components/Repeating/About";
import AreasWeServe from "../components/Repeating/AreasWeServe";
import Resources from "../components/Repeating/Resources";
import CallToAction from "../components/Repeating/CTA";
import ButtonSolid from "../components/Button/ButtonSolid";
import ButtonGhost from "../components/Button/ButtonGhost";

const Page = ({ data }) => {
  //const [modalTabIndex, setModalTabIndex] = useState(0);

  return (
    <Layout headerHasBorder={true}>
      <SearchEngineOptimization
        title="Palm Desert Dialysis Clinic & Nephrologists"
        description="Looking for a top Palm Desert dialysis clinic & nephrologists? At the Kidney Institutes, we specialize in the diagnosis and treatment of kidney disease."
        // openGraphImage={data.openGraphImage.publicURL}
        // twitterOpenGraphImage={data.twitterOpenGraphImage.publicURL}

        canonical="https://www.kidneyinstitutes.com/palm-desert"
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": "https://www.kidneyinstitutes.com/palm-desert#webpage",
              url: "https://www.kidneyinstitutes.com/palm-desert",
              name: "Palm Desert Dialysis Clinic & Nephrologists",
              isPartOf: {
                "@id": "https://www.kidneyinstitutes.com/#website",
              },
              inLanguage: "en-US",
              primaryImageOfPage: {
                "@id":
                  "https://www.kidneyinstitutes.com/palm-desert#primaryimage",
              },
              about: {
                "@id": "https://www.kidneyinstitutes.com/palm-desert#place",
              },
              mentions: [
                {
                  "@id":
                    "https://www.kidneyinstitutes.com/#location-rancho-mirage",
                },
                {
                  "@id": "https://www.kidneyinstitutes.com/#location-la-quinta",
                },
                {
                  "@id":
                    "https://www.kidneyinstitutes.com/#location-nephrology-la-quinta",
                },
              ],
            },
            {
              "@type": "ImageObject",
              "@id":
                "https://www.kidneyinstitutes.com/palm-desert#primaryimage",
              url: "https://www.kidneyinstitutes.com/static/fc3ba5b7525e309df82a0223de52bda2/41c5c/hero-doctor.png",
              contentUrl:
                "https://www.kidneyinstitutes.com/static/fc3ba5b7525e309df82a0223de52bda2/41c5c/hero-doctor.png",
            },
            {
              "@type": "City",
              name: "Palm Desert",
              containedInPlace: {
                "@type": "State",
                name: "California",
              },
              "@id": "https://www.kidneyinstitutes.com/palm-desert#place",
            },
            {
              "@type": "MedicalClinic",
              "@id": "https://www.kidneyinstitutes.com/#location-rancho-mirage",
              name: "Kidney Institute at Eisenhower Medical Center",
              url: "https://www.kidneyinstitutes.com/rancho-mirage-kidney-institute",
            },
            {
              "@type": "MedicalClinic",
              "@id": "https://www.kidneyinstitutes.com/#location-la-quinta",
              name: "La Quinta Kidney Center",
              url: "https://www.kidneyinstitutes.com/la-quinta-kidney-center",
            },
            {
              "@type": "MedicalClinic",
              "@id":
                "https://www.kidneyinstitutes.com/#location-nephrology-la-quinta",
              name: "Coachella Valley Nephrology - La Quinta",
              url: "https://www.kidneyinstitutes.com/rancho-mirage-coachella-valley-nephrology",
            },
            {
              "@type": "MedicalBusiness",
              "@id": "https://www.kidneyinstitutes.com/#organization",
              name: "Kidney Institute of the Desert",
              url: "https://www.kidneyinstitutes.com/",
              logo: {
                "@id": "https://www.kidneyinstitutes.com/#logo",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://www.kidneyinstitutes.com/#website",
              url: "https://www.kidneyinstitutes.com/",
              name: "Kidney Institute of the Desert",
            },
            {
              "@type": "ImageObject",
              "@id": "https://www.kidneyinstitutes.com/#logo",
              url: "https://www.kidneyinstitutes.com/static/ea94a5b767f30e2decfc8f9e920d35db/Logo-grayscale%20stacked.png",
            },
          ],
        }}
      />

      <Background
        backgroundImages={data.heroDesktop.childImageSharp.gatsbyImageData}
        mobileRemoveBackground={true}
        padding="pt-4 md:pt-36 md:pb-56"
        className="mb-8 md:mb-14"
      >
        <div className="max-w-2xl">
          <h1 className="text-secondary-900 md:text-white text-mobile-7xl md:text-7xl font-extrabold mb-4 md:mb-6">
            Expert Desert Nephrology & Kidney Care. Serving Palm Desert Since
            1987.
          </h1>
          <p className="text-xl md:text-white font-semibold mb-7">
            Top physicians and specialists serving four dialysis clinics and two
            nephrology locations.
          </p>

          <ButtonSolid
            modal="modal-contact"
            icon="fas fa-map-marker-alt"
            text="Find a Location"
          />
        </div>
      </Background>

      <div className="relative md:hidden mb-14">
        <GatsbyImage image={data.heroMobile.childImageSharp.gatsbyImageData} />
        <div className="absolute bottom-0 right-0 w-3/4">
          <GatsbyImage image={data.doctor.childImageSharp.gatsbyImageData} />
        </div>
      </div>

      <section className="mb-20 md:mb-32">
        <div className="container">
          <header className="md:text-center md:max-w-[800px] md:mx-auto mb-20 md:mb-32">
            <div className="hidden md:block">
              <img
                src={data.introVector.publicURL}
                alt="Heart icon"
                width="80"
                className="mb-6 mx-auto"
              />
            </div>
            <h2>Palm Desert Dialysis Clinic & Desert Nephrologists</h2>
            <p>
              At Kidney Institute of the Desert, we’re a team of friendly kidney
              doctors and dialysis clinic nurses with years of experience caring
              for patients. Our goal is to help educate our patients about their
              kidney care and support them with treatments that are as
              comfortable and stress-free as possible.
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-y-6 md:gap-x-10 lg:gap-x-20 items-center mb-20 md:mb-32">
            <div className="order-2 md:order-1">
              <h2>Easy Access to Dialysis Services</h2>
              <p>
                Our number one priority is a patient’s health and comfort,
                whether their treatment involves hemodialysis or peritoneal
                dialysis. Our Palm Desert kidney specialists provide expert,
                supportive services for those who are living with kidney
                disease. Palm Desert dialysis patients can be treated at our
                nearby{" "}
                <a
                  href="https://www.kidneyinstitutes.com/rancho-mirage-kidney-institute"
                  className="font-bold text-[#162d6d]"
                >
                  Rancho Mirage clinic at Eisenhower Medical Center
                </a>
                , or at our{" "}
                <a
                  href="https://www.kidneyinstitutes.com/indio-kidney-institute"
                  className="font-bold text-[#162d6d]"
                >
                  Indio
                </a>{" "}
                and{" "}
                <a
                  href="https://www.kidneyinstitutes.com/la-quinta-kidney-center"
                  className="font-bold text-[#162d6d]"
                >
                  La Quinta
                </a>{" "}
                clinics.
              </p>
              <ButtonGhost href="/dialysis-services" text="Learn More" />
            </div>
            <div className="order-1 md:order-2">
              <GatsbyImage
                image={data.dialysisServices.childImageSharp.gatsbyImageData}
                width="560"
                className="rounded-xl md:rounded-4xl"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-y-6 md:gap-x-10 lg:gap-x-20 items-center">
            <div>
              <GatsbyImage
                image={data.nephrologyExperts.childImageSharp.gatsbyImageData}
                width="560"
                className="rounded-xl md:rounded-4xl"
              />
            </div>
            <div>
              <h2>Top Desert Nephrology Experts by Your Side</h2>
              <p>
                At Kidney Institute of the Desert, our physicians have spent
                years building their expertise and knowledge in kidney care both
                here and around the world. Our Palm Desert nephrologists are
                specialists in both the diagnosis and treatment of kidney
                disease.
              </p>
              <ButtonGhost href="/nephrology" text="Learn More" />
            </div>
          </div>
        </div>
      </section>

      <AreasWeServe />
      <ValueProps />
      <Resources />
      <Testimonials />
      <About />
      <CallToAction />
    </Layout>
  );
};

export const query = graphql`
  {
    openGraphImage: file(
      relativePath: { eq: "open-graph/facebook/Global.jpg" }
    ) {
      publicURL
    }
    twitterOpenGraphImage: file(
      relativePath: { eq: "open-graph/twitter/Global.jpg" }
    ) {
      publicURL
    }
    heroDesktop: file(relativePath: { eq: "home/1.0-hero-whole.jpg" }) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH)
      }
    }
    heroMobile: file(relativePath: { eq: "home/1.0-hero-curve-mobile.jpg" }) {
      childImageSharp {
        gatsbyImageData(layout: FULL_WIDTH)
      }
    }
    doctor: file(relativePath: { eq: "home/hero-doctor.png" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, placeholder: NONE)
      }
    }
    introVector: file(relativePath: { eq: "home/intro.svg" }) {
      publicURL
    }
    nephrologyExperts: file(
      relativePath: { eq: "home/nephrology-experts.jpg" }
    ) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 560)
      }
    }
    dialysisServices: file(relativePath: { eq: "home/dialysis-services.jpg" }) {
      childImageSharp {
        gatsbyImageData(layout: CONSTRAINED, width: 560)
      }
    }
  }
`;
export default Page;
